import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Building2, Plus, Edit2, Trash2, CheckCircle2, XCircle,
    Users, FolderTree, FileText, CalendarDays, AlertCircle,
    Loader2, Search, Hospital, Globe, Briefcase, Landmark, Shield,
    RotateCcw, Archive
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
import OrgUsersModal, { OrgUserPicker } from '../../components/ui/OrgUsersModal';

const ICONS = [
    { name: 'Building2', label: 'Bino', icon: Building2 },
    { name: 'Hospital', label: 'Tibbiyot', icon: Hospital },
    { name: 'Globe', label: 'Global / Ijtimoiy', icon: Globe },
    { name: 'Briefcase', label: 'Biznes', icon: Briefcase },
    { name: 'Landmark', label: 'Ta\'lim / Tashkilot', icon: Landmark },
    { name: 'Shield', label: 'Xavfsizlik', icon: Shield },
];

const COLORS = [
    { name: 'emerald', bg: 'bg-emerald-500', text: 'text-emerald-500', border: 'border-emerald-500/20' },
    { name: 'blue', bg: 'bg-blue-500', text: 'text-blue-500', border: 'border-blue-500/20' },
    { name: 'purple', bg: 'bg-purple-500', text: 'text-purple-500', border: 'border-purple-500/20' },
    { name: 'amber', bg: 'bg-amber-500', text: 'text-amber-500', border: 'border-amber-500/20' },
    { name: 'rose', bg: 'bg-rose-500', text: 'text-rose-500', border: 'border-rose-500/20' },
    { name: 'indigo', bg: 'bg-indigo-500', text: 'text-indigo-500', border: 'border-indigo-500/20' },
];

const INITIAL_FORM = {
    name_uz: '',
    name_ru: '',
    name_en: '',
    code: '',
    description_uz: '',
    description_ru: '',
    description_en: '',
    icon_name: 'Building2',
    color: 'emerald',
    order: 0,
    is_active: true,
};

export default function OrganizationsPage() {
    const { user } = useAuth();
    const { refreshOrganizations } = useOrganization();
    const { t } = useTranslation();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editingOrg, setEditingOrg] = useState(null);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [saving, setSaving] = useState(false);
    const [deleteModalOrg, setDeleteModalOrg] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [restoreModalOrg, setRestoreModalOrg] = useState(null);
    const [restoring, setRestoring] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    // A'zolarni boshqarish oynasi
    const [membersOrg, setMembersOrg] = useState(null);
    // Yaratishda biriktiriladigan foydalanuvchilar
    const [createUserIds, setCreateUserIds] = useState([]);

    const lang = i18n.language || 'uz';

    const fetchAllOrganizations = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            setError(null);
            const res = await api.get('/organizations?include_all=true');
            setOrganizations(res.data || []);
        } catch (err) {
            setError(err.message || "Tashkilotlarni yuklashda xatolik yuz berdi");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllOrganizations();
    }, []);

    const openCreateModal = () => {
        setEditingOrg(null);
        setCreateUserIds([]);
        setFormData({
            ...INITIAL_FORM,
            order: organizations.length + 1,
        });
        setModalOpen(true);
    };

    const openEditModal = (org) => {
        setEditingOrg(org);
        setFormData({
            name_uz: org.name_uz || '',
            name_ru: org.name_ru || '',
            name_en: org.name_en || '',
            code: org.code || '',
            description_uz: org.description_uz || '',
            description_ru: org.description_ru || '',
            description_en: org.description_en || '',
            icon_name: org.icon_name || 'Building2',
            color: org.color || 'emerald',
            order: org.order || 0,
            is_active: org.is_active !== false,
        });
        setModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.name_uz.trim()) {
            alert(t('organizations.error_name_required', "O'zbekcha nom kiritilishi shart"));
            return;
        }

        try {
            setSaving(true);
            const payload = {
                ...formData,
                name_uz: formData.name_uz.trim(),
                name_ru: formData.name_ru?.trim() || null,
                name_en: formData.name_en?.trim() || null,
                code: formData.code?.trim().toLowerCase() || null,
                order: parseInt(formData.order) || 0,
            };

            if (editingOrg) {
                await api.put(`/organizations/${editingOrg.id}`, payload);
            } else {
                await api.post('/organizations', { ...payload, user_ids: createUserIds });
            }

            setModalOpen(false);
            await fetchAllOrganizations();
            await refreshOrganizations();
        } catch (err) {
            alert(err.message || "Saqlashda xatolik yuz berdi");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteModalOrg) return;
        try {
            setDeleting(true);
            await api.delete(`/organizations/${deleteModalOrg.id}`);
            setDeleteModalOrg(null);
            await fetchAllOrganizations();
            await refreshOrganizations();
        } catch (err) {
            alert(err.message || "O'chirishda xatolik yuz berdi");
        } finally {
            setDeleting(false);
        }
    };

    const handleRestore = async () => {
        if (!restoreModalOrg) return;
        try {
            setRestoring(true);
            await api.post(`/organizations/${restoreModalOrg.id}/restore`);
            setRestoreModalOrg(null);
            await fetchAllOrganizations();
            await refreshOrganizations();
        } catch (err) {
            alert(err.response?.data?.detail || err.message || "Qayta tiklashda xatolik yuz berdi");
        } finally {
            setRestoring(false);
        }
    };

    if (!user?.is_superadmin && !user?.can_manage_organizations) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-center">
                <AlertCircle className="w-12 h-12 text-error mb-4" />
                <h2 className="text-xl font-bold text-primary">
                    {t('admin.access_denied', "Ruxsat etilmagan")}
                </h2>
                <p className="text-muted mt-2">
                    {t('organizations.superadmin_only', "Bu bo'lim faqat Bosh Administrator (Super Admin) uchun ochiq")}
                </p>
            </div>
        );
    }

    const filteredOrgs = organizations.filter(org => {
        if (statusFilter === 'active' && org.is_active === false) return false;
        if (statusFilter === 'archived' && org.is_active !== false) return false;

        const q = search.toLowerCase();
        return (
            org.name_uz?.toLowerCase().includes(q) ||
            org.name_ru?.toLowerCase().includes(q) ||
            org.name_en?.toLowerCase().includes(q) ||
            org.code?.toLowerCase().includes(q)
        );
    });

    const activeCount = organizations.filter(o => o.is_active !== false).length;
    const archivedCount = organizations.filter(o => o.is_active === false).length;

    return (
        <div className="space-y-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-primary flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-brand/10 text-brand">
                            <Building2 className="w-6 h-6" />
                        </div>
                        {t('organizations.title', "Tashkilotlar va Bo'limlar")}
                    </h1>
                    <p className="text-sm text-muted mt-1">
                        {t('organizations.subtitle', "Platformadagi alohida kompaniyalar, ularning kategoriyalari, hujjatlari va loyihalarini boshqarish")}
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={openCreateModal}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand text-inverse text-sm font-semibold hover:bg-brand/90 shadow-brand-sm transition-all"
                    >
                        <Plus className="w-4 h-4" />
                        {t('organizations.add_new', "Yangi bo'lim qo'shish")}
                    </button>
                </div>
            </div>

            {/* Search and Status filter tabs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="relative max-w-md w-full">
                    <Search className="w-4 h-4 text-muted absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('organizations.search_placeholder', "Tashkilot nomi yoki kodi bo'yicha qidirish...")}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                    />
                </div>

                <div className="flex items-center p-1 rounded-xl bg-surface-2 border border-border text-xs font-semibold self-start sm:self-auto">
                    <button
                        type="button"
                        onClick={() => setStatusFilter('all')}
                        className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                            statusFilter === 'all'
                                ? 'bg-surface text-primary shadow-sm'
                                : 'text-muted hover:text-primary'
                        }`}
                    >
                        <span>{t('organizations.tab_all', 'Barchasi')}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-2 text-muted">
                            {organizations.length}
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('active')}
                        className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                            statusFilter === 'active'
                                ? 'bg-surface text-primary shadow-sm'
                                : 'text-muted hover:text-primary'
                        }`}
                    >
                        <span>{t('organizations.tab_active', 'Faol')}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold">
                            {activeCount}
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('archived')}
                        className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                            statusFilter === 'archived'
                                ? 'bg-surface text-primary shadow-sm'
                                : 'text-muted hover:text-primary'
                        }`}
                    >
                        <span>{t('organizations.tab_archived', 'Arxiv')}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 font-bold">
                            {archivedCount}
                        </span>
                    </button>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center p-20">
                    <Loader2 className="w-8 h-8 text-brand animate-spin" />
                </div>
            ) : error ? (
                <div className="p-6 rounded-2xl bg-error/10 border border-error/20 text-error flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            ) : filteredOrgs.length === 0 ? (
                <div className="p-12 text-center rounded-2xl bg-surface border border-border">
                    <Building2 className="w-12 h-12 text-muted mx-auto mb-3 opacity-40" />
                    <p className="text-base font-semibold text-primary">{t('organizations.not_found', "Tashkilotlar topilmadi")}</p>
                    <p className="text-sm text-muted mt-1">{t('organizations.create_first', "Yangi bo'lim qo'shish tugmasi orqali yangisini yarating")}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filteredOrgs.map((org) => {
                        const IconComp = (org.icon_name && ICONS.find(i => i.name === org.icon_name)?.icon) || Building2;
                        const orgColor = org.color || 'emerald';
                        const colorObj = COLORS.find(c => c.name === orgColor) || COLORS[0];

                        return (
                            <motion.div
                                key={org.id}
                                layout
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`bg-surface border rounded-2xl p-5 transition-all flex flex-col justify-between shadow-sm hover:shadow-md ${
                                    org.is_active
                                        ? 'border-border hover:border-brand/40'
                                        : 'border-dashed border-amber-500/40 bg-surface/70'
                                }`}
                            >
                                <div>
                                    {/* Card Header */}
                                    <div className="flex items-start justify-between gap-3 mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${colorObj.bg}/10 ${colorObj.text} border ${colorObj.border}`}>
                                                {org.logo_url ? (
                                                    <img src={org.logo_url} alt="" className="w-8 h-8 object-contain" />
                                                ) : (
                                                    <IconComp className="w-6 h-6" />
                                                )}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-base text-primary leading-snug">
                                                    {org[`name_${lang}`] || org.name_uz || org.name_ru || org.name_en}
                                                </h3>
                                                {org.code && (
                                                    <span className="inline-block mt-0.5 px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-surface-2 text-muted uppercase">
                                                        {org.code}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
                                            org.is_active
                                                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                                : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                                        }`}>
                                            {org.is_active ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                                            {org.is_active ? t('organizations.status_active', 'Faol') : t('organizations.status_archived', 'Arxivda')}
                                        </span>
                                    </div>

                                    {/* Description */}
                                    {(org.description_uz || org.description_ru || org.description_en) && (
                                        <p className="text-xs text-muted line-clamp-2 mb-4 leading-relaxed">
                                            {org[`description_${lang}`] || org.description_uz || org.description_ru || org.description_en}
                                        </p>
                                    )}

                                    {/* Stats grid */}
                                    <div className="grid grid-cols-2 gap-2 mb-4 pt-3 border-t border-border">
                                        <div className="p-2.5 rounded-xl bg-surface-2 flex items-center gap-2.5">
                                            <FolderTree className="w-4 h-4 text-brand" />
                                            <div>
                                                <div className="text-xs font-bold text-primary">{org.categories_count || 0}</div>
                                                <div className="text-[10px] text-muted">{t('nav.categories', 'Kategoriyalar')}</div>
                                            </div>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-surface-2 flex items-center gap-2.5">
                                            <FileText className="w-4 h-4 text-blue-500" />
                                            <div>
                                                <div className="text-xs font-bold text-primary">{org.documents_count || 0}</div>
                                                <div className="text-[10px] text-muted">{t('panel.documents', 'Hujjatlar')}</div>
                                            </div>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-surface-2 flex items-center gap-2.5">
                                            <CalendarDays className="w-4 h-4 text-amber-500" />
                                            <div>
                                                <div className="text-xs font-bold text-primary">{org.gantt_projects_count || 0}</div>
                                                <div className="text-[10px] text-muted">{t('panel.gantt_chart', 'Loyihalar')}</div>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setMembersOrg(org)}
                                            title={t('organizations.members_manage')}
                                            className="p-2.5 rounded-xl bg-surface-2 flex items-center gap-2.5 text-left hover:bg-purple-500/10 hover:ring-1 hover:ring-purple-500/30 transition-all"
                                        >
                                            <Users className="w-4 h-4 text-purple-500" />
                                            <div>
                                                <div className="text-xs font-bold text-primary">{org.users_count || 0}</div>
                                                <div className="text-[10px] text-muted">{t('nav.users', 'Userlar')}</div>
                                            </div>
                                        </button>
                                    </div>
                                </div>

                                {/* Card Footer Actions */}
                                <div className="flex items-center justify-between pt-3 border-t border-border mt-1">
                                    <span className="text-[11px] text-muted">
                                        {t('common.order', 'Tartib')}: #{org.order || 0}
                                    </span>
                                    <div className="flex items-center gap-1.5">
                                        <button
                                            id={`org-users-btn-${org.code || org.id}`}
                                            onClick={() => setMembersOrg(org)}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-secondary hover:text-brand hover:bg-surface-2 transition-colors"
                                            title={t('organizations.members_manage')}
                                        >
                                            <Users className="w-4 h-4" />
                                            {t('organizations.members_title')}
                                        </button>
                                        <button
                                            onClick={() => openEditModal(org)}
                                            className="p-1.5 rounded-lg text-secondary hover:text-brand hover:bg-surface-2 transition-colors"
                                            title={t('common.edit', 'Tahrirlash')}
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        {org.is_active ? (
                                            <button
                                                id={`org-archive-btn-${org.code || org.id}`}
                                                onClick={() => setDeleteModalOrg(org)}
                                                className="p-1.5 rounded-lg text-secondary hover:text-amber-500 hover:bg-amber-500/10 transition-colors"
                                                title={t('organizations.archive_action', "Arxivlash")}
                                            >
                                                <Archive className="w-4 h-4" />
                                            </button>
                                        ) : (
                                            <button
                                                id={`org-restore-btn-${org.code || org.id}`}
                                                onClick={() => setRestoreModalOrg(org)}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors"
                                                title={t('organizations.restore_action', "Tiklash")}
                                            >
                                                <RotateCcw className="w-3.5 h-3.5" />
                                                <span>{t('organizations.restore_action', "Tiklash")}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}

            {/* Create / Edit Modal */}
            <AnimatePresence>
                {modalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden"
                        >
                            <div className="p-6 border-b border-border flex items-center justify-between">
                                <h2 className="text-lg font-bold text-primary flex items-center gap-2">
                                    <Building2 className="w-5 h-5 text-brand" />
                                    {editingOrg ? t('organizations.edit_title', "Tashkilotni tahrirlash") : t('organizations.add_title', "Yangi bo'lim qo'shish")}
                                </h2>
                                <button
                                    onClick={() => setModalOpen(false)}
                                    className="p-1 rounded-lg text-muted hover:text-primary hover:bg-surface-2"
                                >
                                    <XCircle className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSave} className="p-6 space-y-5">
                                {/* Name fields: UZ, RU, EN */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-secondary mb-1.5">
                                            {t('organizations.name_uz', "Nomi (O'zbekcha)")} <span className="text-error">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name_uz}
                                            onChange={(e) => setFormData({ ...formData, name_uz: e.target.value })}
                                            placeholder="Masalan: Central Asian University"
                                            className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-surface-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-secondary mb-1.5">
                                                {t('organizations.name_ru', "Nomi (Русский)")}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name_ru}
                                                onChange={(e) => setFormData({ ...formData, name_ru: e.target.value })}
                                                placeholder="Центрально-Азиатский Университет"
                                                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-surface-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-secondary mb-1.5">
                                                {t('organizations.name_en', "Nomi (English)")}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name_en}
                                                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                                                placeholder="Central Asian University"
                                                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-surface-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Code and Order */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-secondary mb-1.5">
                                            {t('organizations.code', "Identifikator / Kod (unikal)")}
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.code}
                                            onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
                                            placeholder="cau, akfa_medline, socials"
                                            className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-surface-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand/40"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-secondary mb-1.5">
                                            {t('common.order', "Tartib raqami")}
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.order}
                                            onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                                            className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-surface-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                                        />
                                    </div>
                                </div>

                                {/* Icon and Color Selection */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-secondary mb-2">
                                            {t('organizations.icon', "Belgi (Icon)")}
                                        </label>
                                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                                            {ICONS.map(({ name, label, icon: Icon }) => (
                                                <button
                                                    key={name}
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, icon_name: name })}
                                                    className={`p-2.5 rounded-xl border flex flex-col items-center gap-1.5 transition-all ${
                                                        formData.icon_name === name
                                                            ? 'border-brand bg-brand/10 text-brand font-bold ring-2 ring-brand/30'
                                                            : 'border-border bg-surface-2 text-muted hover:text-primary'
                                                    }`}
                                                >
                                                    <Icon className="w-5 h-5" />
                                                    <span className="text-[10px] truncate max-w-full">{label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-secondary mb-2">
                                            {t('organizations.theme_color', "Mavzu rangi")}
                                        </label>
                                        <div className="flex items-center gap-3">
                                            {COLORS.map(({ name, bg }) => (
                                                <button
                                                    key={name}
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, color: name })}
                                                    className={`w-8 h-8 rounded-full ${bg} transition-all flex items-center justify-center ${
                                                        formData.color === name ? 'ring-4 ring-brand/40 scale-110 shadow-sm' : 'opacity-70 hover:opacity-100'
                                                    }`}
                                                >
                                                    {formData.color === name && (
                                                        <CheckCircle2 className="w-4 h-4 text-white" />
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Description (UZ) */}
                                <div>
                                    <label className="block text-xs font-bold text-secondary mb-1.5">
                                        {t('common.description', "Tavsif (O'zbekcha)")}
                                    </label>
                                    <textarea
                                        rows={2}
                                        value={formData.description_uz}
                                        onChange={(e) => setFormData({ ...formData, description_uz: e.target.value })}
                                        placeholder="Bo'lim yoki tashkilot faoliyati haqida qisqacha ma'lumot..."
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-surface-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 resize-none"
                                    />
                                </div>

                                {/* Active toggle */}
                                <div className="flex items-center gap-3 pt-2">
                                    <input
                                        type="checkbox"
                                        id="org_is_active"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        className="w-4 h-4 rounded text-brand focus:ring-brand accent-brand cursor-pointer"
                                    />
                                    <label htmlFor="org_is_active" className="text-sm font-semibold text-primary cursor-pointer">
                                        {t('organizations.active_status', "Faol holatda saqlash (foydalanuvchilar kira oladi)")}
                                    </label>
                                </div>

                                {/* Foydalanuvchilarni biriktirish — faqat yaratishda (tahrirda kartadagi "Foydalanuvchilar" tugmasi) */}
                                {!editingOrg && (
                                    <div>
                                        <label className="block text-xs font-bold text-secondary mb-1.5">
                                            {t('organizations.members_assign_on_create')}
                                        </label>
                                        <OrgUserPicker
                                            value={createUserIds}
                                            onChange={setCreateUserIds}
                                            excludeIds={user?.id ? [user.id] : []}
                                        />
                                        <p className="text-[11px] text-muted mt-1.5">{t('organizations.members_assign_hint')}</p>
                                    </div>
                                )}

                                {editingOrg && (
                                    <button
                                        type="button"
                                        onClick={() => { setModalOpen(false); setMembersOrg(editingOrg); }}
                                        className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-border bg-surface-2 hover:border-brand/40 transition-colors text-left"
                                    >
                                        <span className="flex items-center gap-2.5 text-sm font-semibold text-primary">
                                            <Users className="w-4 h-4 text-purple-500" />
                                            {t('organizations.members_manage')}
                                        </span>
                                        <span className="text-xs text-muted">{editingOrg.users_count || 0}</span>
                                    </button>
                                )}

                                {/* Modal Actions */}
                                <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                                    <button
                                        type="button"
                                        onClick={() => setModalOpen(false)}
                                        className="px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary hover:bg-surface-2 transition-colors"
                                    >
                                        {t('common.cancel', 'Bekor qilish')}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand text-inverse text-sm font-semibold hover:bg-brand/90 shadow-brand-sm transition-all disabled:opacity-50"
                                    >
                                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                                        {editingOrg ? t('common.save', 'Saqlash') : t('common.create', 'Yaratish')}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* A'zolarni boshqarish oynasi */}
            <AnimatePresence>
                {membersOrg && (
                    <OrgUsersModal
                        org={membersOrg}
                        onClose={async (changed) => {
                            setMembersOrg(null);
                            if (changed) {
                                await fetchAllOrganizations(true);
                                await refreshOrganizations();
                            }
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Archive Confirmation Modal */}
            <AnimatePresence>
                {deleteModalOrg && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4"
                        >
                            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto">
                                <Archive className="w-6 h-6" />
                            </div>

                            <div className="text-center">
                                <h3 className="text-lg font-bold text-primary">
                                    {t('organizations.delete_confirm_title', "Tashkilotni arxivlash")}
                                </h3>
                                <p className="text-xs text-muted mt-2 leading-relaxed">
                                    <span className="font-bold text-primary">{deleteModalOrg.name_uz || deleteModalOrg.name_ru}</span> {t('organizations.delete_confirm_desc', "tashkiloti faolsizlantiriladi va arxivga o'tkaziladi. Unga tegishli barcha kategoriyalar, hujjatlar va loyihalar to'liq saqlanadi va istalgan payt qayta tiklash mumkin.")}
                                </p>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    type="button"
                                    disabled={deleting}
                                    onClick={() => setDeleteModalOrg(null)}
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary hover:bg-surface-2 transition-colors"
                                >
                                    {t('common.cancel', 'Bekor qilish')}
                                </button>
                                <button
                                    type="button"
                                    disabled={deleting}
                                    onClick={handleDelete}
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
                                >
                                    {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                                    {t('organizations.archive_action', "Arxivlash")}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Restore Confirmation Modal */}
            <AnimatePresence>
                {restoreModalOrg && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4"
                        >
                            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mx-auto">
                                <RotateCcw className="w-6 h-6" />
                            </div>

                            <div className="text-center">
                                <h3 className="text-lg font-bold text-primary">
                                    {t('organizations.restore_confirm_title', "Tashkilotni qayta tiklash")}
                                </h3>
                                <p className="text-xs text-muted mt-2 leading-relaxed">
                                    <span className="font-bold text-primary">{restoreModalOrg.name_uz || restoreModalOrg.name_ru}</span> {t('organizations.restore_confirm_desc', "tashkiloti qayta faollashtiriladi va tizimda yana to'liq ko'rina boshlaydi.")}
                                </p>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    type="button"
                                    disabled={restoring}
                                    onClick={() => setRestoreModalOrg(null)}
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary hover:bg-surface-2 transition-colors"
                                >
                                    {t('common.cancel', 'Bekor qilish')}
                                </button>
                                <button
                                    type="button"
                                    disabled={restoring}
                                    onClick={handleRestore}
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                >
                                    {restoring && <Loader2 className="w-4 h-4 animate-spin" />}
                                    {t('organizations.restore_action', "Tiklash")}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
